import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { desktopTheme as t } from '../../styles/desktopTheme';
import {
  getConversationByIdForDizee,
  getConversationHistoryForDizee,
  processQuestionForDizee,
} from '../Manager/api/openaiService';

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 20px;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  height: 100%;
  min-height: 0;
  align-items: stretch;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: ${t.panel};
  border: 1px solid ${t.borderSubtle};
  border-radius: 14px;
  overflow: hidden;
  box-shadow: ${t.shadowCard};
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

const PanelHeader = styled.div`
  padding: 14px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${t.borderSubtle};
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${t.textPrimary};
`;

const SmallButton = styled.button`
  padding: 8px 10px;
  background: transparent;
  border: 1px solid ${t.borderSubtle};
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${t.borderStrong};
  }
`;

const List = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px;
`;

const ConversationRow = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 10px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: ${p => (p.$active ? 'rgba(255,255,255,0.06)' : 'transparent')};
  color: ${t.textPrimary};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${t.borderSubtle};
  }
`;

const ConversationTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`;

const ConversationMeta = styled.div`
  font-size: 10px;
  color: ${t.textTertiary};
`;

const ChatWrap = styled(Panel)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

const Messages = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Bubble = styled.div`
  max-width: 78%;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  color: ${t.textPrimary};
  background: ${p => (p.$role === 'user' ? 'rgba(255,255,255,0.08)' : t.surface)};
  border: 1px solid ${t.borderSubtle};
  align-self: ${p => (p.$role === 'user' ? 'flex-end' : 'flex-start')};
`;

const Composer = styled.div`
  border-top: 1px solid ${t.borderSubtle};
  padding: 12px;
  display: flex;
  gap: 10px;
  align-items: flex-end;
`;

const Textarea = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 40px;
  max-height: 140px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${t.borderSubtle};
  color: ${t.textPrimary};
  font-size: 12px;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }
`;

const SendButton = styled.button`
  padding: 10px 14px;
  border-radius: 12px;
  border: none;
  background: ${t.accent};
  color: ${t.textPrimary};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  opacity: ${p => (p.disabled ? 0.6 : 1)};

  &:hover {
    background: ${t.accentHover};
  }
`;

const Hint = styled.div`
  color: ${t.textTertiary};
  font-size: 11px;
  padding: 16px;
`;

const mapBackendMessages = (aiMessages) => {
  if (!Array.isArray(aiMessages)) return [];
  return aiMessages
    .map((m) => ({
      role: m?.type === 'user' ? 'user' : 'assistant',
      content: m?.message || '',
    }))
    .filter((m) => m.content);
};

export default function DesktopChat() {
  const dispatch = useDispatch();
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const endRef = useRef(null);

  const activeTitle = useMemo(() => {
    const c = conversations.find((x) => x?._id === activeConversationId);
    return c?.title || 'Chat';
  }, [conversations, activeConversationId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loadingChat]);

  const loadConversationList = async (selectFirst = true) => {
    setLoadingList(true);
    const result = await getConversationHistoryForDizee();
    if (result?.success) {
      setConversations(result.conversations || []);
      if (selectFirst && !activeConversationId && (result.conversations || []).length > 0) {
        setActiveConversationId(result.conversations[0]._id);
      }
    } else {
      setConversations([]);
    }
    setLoadingList(false);
  };

  const loadConversation = async (conversationId) => {
    if (!conversationId) return;
    setLoadingChat(true);
    const res = await getConversationByIdForDizee(conversationId);
    if (res?.success) {
      setMessages(mapBackendMessages(res.messages));
    } else {
      setMessages([]);
    }
    setLoadingChat(false);
  };

  useEffect(() => {
    loadConversationList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeConversationId) loadConversation(activeConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId]);

  const handleNewChat = () => {
    setActiveConversationId('');
    setMessages([]);
    setInput('');
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loadingChat) return;

    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setLoadingChat(true);

    const historyForApi = nextMessages.map((m) => ({
      type: m.role,
      content: m.content,
    }));

    const res = await processQuestionForDizee(text, historyForApi, dispatch, activeConversationId, []);
    if (res?.success) {
      setMessages((cur) => [...cur, { role: 'assistant', content: res.response }]);
      if (res.conversationId && res.conversationId !== activeConversationId) {
        setActiveConversationId(res.conversationId);
        await loadConversationList(false);
      }
    } else {
      setMessages((cur) => [
        ...cur,
        { role: 'assistant', content: 'Something went wrong. Please try again.' },
      ]);
    }

    setLoadingChat(false);
  };

  return (
    <PageGrid>
      <Panel>
        <PanelHeader>
          <Title>Conversations</Title>
          <SmallButton onClick={handleNewChat}>New chat</SmallButton>
        </PanelHeader>
        <List>
          {loadingList ? (
            <Hint>Loading…</Hint>
          ) : conversations.length === 0 ? (
            <Hint>No conversations yet.</Hint>
          ) : (
            conversations.map((c) => (
              <ConversationRow
                key={c._id}
                $active={c._id === activeConversationId}
                onClick={() => setActiveConversationId(c._id)}
              >
                <ConversationTitle>{c.title || 'Untitled'}</ConversationTitle>
                <ConversationMeta>
                  {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}
                </ConversationMeta>
              </ConversationRow>
            ))
          )}
        </List>
      </Panel>

      <ChatWrap>
        <PanelHeader>
          <Title>{activeTitle}</Title>
          <SmallButton onClick={() => loadConversationList(false)}>Refresh</SmallButton>
        </PanelHeader>

        <Messages>
          {messages.length === 0 ? (
            <Hint>
              Ask Dizee anything. This is the desktop chat view.
            </Hint>
          ) : (
            messages.map((m, idx) => (
              <Bubble key={idx} $role={m.role}>
                {m.content}
              </Bubble>
            ))
          )}
          {loadingChat ? <Hint>Thinking…</Hint> : null}
          <div ref={endRef} />
        </Messages>

        <Composer>
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything…"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <SendButton onClick={handleSend} disabled={!input.trim() || loadingChat}>
            Send
          </SendButton>
        </Composer>
      </ChatWrap>
    </PageGrid>
  );
}


